import { CalendarPlus, Home, Inbox, MessageSquare } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';

import { ClientPage } from '@/components/client/ClientPage';
import { Button } from '@/components/ui/button';
import { EmptyState, ErrorState } from '@/components/ui/empty-state';
import { Field, FormError, Label } from '@/components/ui/field';
import { RequiredFieldsNote } from '@/components/ui/form-section';
import { Pagination } from '@/components/ui/pagination';
import { StatusBadge, type StatusTone } from '@/components/ui/status-badge';
import {
  useClientJobRequests,
  useClientProperties,
  useCreateJobRequest,
  useRequestableServices,
} from '@/hooks/client-portal';
import { ApiError } from '@/lib/api';
import { formatDay, formatDuration, formatInstant, formatWallTime } from '@/lib/dates';
import { useToast } from '@/lib/toast-context';
import type { ClientJobRequestView, JobRequestStatusValue } from '@shared/schemas/job-request';
import { DEFAULT_PAGE_SIZE } from '@shared/schemas/pagination';

/*
 * /portal/request — ROADMAP.md §5.4.
 *
 * **Two panes: the form on the left, the history on the right.** That is the
 * prototype's layout and it is worth keeping (FINDINGS.md §10.5) — the client
 * sees what they asked for and what happened to it in one place, so "did that go
 * through?" is answered by looking rather than by ringing.
 *
 * The one thing the prototype gets wrong here is P16: a declined request shows
 * the client nothing at all. `reviewNote` is rendered below on **approvals and
 * declines both**, which is why the column is not called `decline_reason`.
 */

/** One word for the state — `declined`, never "Not Approved" (P11, P18). */
const REQUEST_TONES: Record<JobRequestStatusValue, StatusTone> = {
  pending: 'pending',
  approved: 'completed',
  declined: 'cancelled',
};

export function RequestCleaningPage() {
  const [page, setPage] = useState(1);
  const requests = useClientJobRequests({ page });

  return (
    <ClientPage
      title="Request a Cleaning"
      description="Tell us what you need and when suits. We will confirm a date with you."
    >
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <section>
          <h2 className="mb-3 text-h2 text-ink-900">What do you need?</h2>
          <RequestForm />
        </section>

        {/* A named region so the two panes are navigable, and so a test can
            scope to one of them — the select and the history carry the same
            service names on purpose (P17). */}
        <section aria-label="What you have asked for">
          <h2 className="mb-3 text-h2 text-ink-900">What you have asked for</h2>

          {requests.error ? (
            <div className="rounded-lg border border-ink-300 bg-white shadow-card">
              <ErrorState
                title="We could not load your requests"
                hint="Something went wrong on our side. Try again — and ring the office on (435) 238-3286 if it keeps happening."
                onRetry={() => {
                  void requests.refetch();
                }}
              />
            </div>
          ) : requests.isPending ? (
            <div aria-busy="true">
              <span className="sr-only">Loading your requests</span>
              <div aria-hidden="true" className="h-40 animate-pulse rounded-lg bg-ink-100" />
            </div>
          ) : requests.data && requests.data.data.length > 0 ? (
            <>
              <ul className="space-y-3">
                {requests.data.data.map((entry) => (
                  <RequestCard key={entry.id} request={entry} />
                ))}
              </ul>
              <Pagination
                page={requests.data.page}
                pageSize={requests.data.pageSize || DEFAULT_PAGE_SIZE}
                total={requests.data.total}
                onPageChange={setPage}
                itemNoun="requests"
              />
            </>
          ) : (
            <div className="rounded-lg border border-ink-300 bg-white shadow-card">
              <EmptyState
                icon={Inbox}
                title="You have not asked for anything yet."
                hint="Fill in the form and it appears here, with what we said back."
              />
            </div>
          )}
        </section>
      </div>
    </ClientPage>
  );
}

function RequestCard({ request }: { request: ClientJobRequestView }) {
  return (
    <li className="rounded-lg border border-ink-300 bg-white p-4 shadow-card">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          {/* `serviceTypeName` from the server, so the request history and the
              select above cannot call the same service two different things
              (P17). */}
          <p className="text-h3 text-ink-900">
            {request.serviceTypeName ?? (
              <span className="italic text-ink-500">Service not chosen</span>
            )}
          </p>
          <p className="mt-0.5 text-meta text-ink-500">
            Asked {formatInstant(request.submittedAt)}
          </p>
        </div>
        <StatusBadge label={request.statusLabel} tone={REQUEST_TONES[request.status]} />
      </div>

      <dl className="mt-3 space-y-1.5 text-body text-ink-700">
        <div className="flex flex-wrap gap-x-2">
          <dt className="text-ink-500">Address</dt>
          <dd>
            {request.propertyLabel ?? (
              <span className="italic text-ink-500">That address has since been removed</span>
            )}
          </dd>
        </div>
        <div className="flex flex-wrap gap-x-2">
          <dt className="text-ink-500">Preferred</dt>
          <dd>
            {request.preferredDate ? (
              <>
                {formatDay(request.preferredDate)}
                {request.preferredTime ? ` at ${formatWallTime(request.preferredTime)}` : ''}
              </>
            ) : (
              <span className="italic text-ink-500">No date given — we will suggest one</span>
            )}
          </dd>
        </div>
        {request.estimatedDurationMinutes === null ? null : (
          <div className="flex flex-wrap gap-x-2">
            <dt className="text-ink-500">You thought</dt>
            <dd>about {formatDuration(request.estimatedDurationMinutes)}</dd>
          </div>
        )}
      </dl>

      {request.notes ? (
        <p className="mt-3 text-meta text-ink-700">
          <span className="text-ink-500">Your notes: </span>
          {request.notes}
        </p>
      ) : null}

      {/*
        **P16.** The office's note reaches the client on an approval as well as a
        decline, and a decline cannot be saved without one. Two of the four
        declines in the prototype show the client nothing at all.
      */}
      {request.reviewNote ? (
        <p className="mt-3 flex items-start gap-2 rounded-sm border border-ink-300 bg-honey-50 px-3 py-2.5 text-body text-bark-900">
          <MessageSquare aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
          <span>
            <span className="block text-meta font-medium">Note from the team</span>
            {request.reviewNote}
          </span>
        </p>
      ) : null}

      {request.createdJobId ? (
        <p className="mt-3">
          <Link
            to={`/portal/cleans/${request.createdJobId}`}
            className="text-label text-bark-700 underline underline-offset-2"
          >
            See the booked clean{request.createdJobNumber ? ` (${request.createdJobNumber})` : ''}
          </Link>
        </p>
      ) : null}
    </li>
  );
}

function RequestForm() {
  const toast = useToast();
  const properties = useClientProperties();
  const services = useRequestableServices();
  const create = useCreateJobRequest();

  const [propertyId, setPropertyId] = useState('');
  const [serviceTypeId, setServiceTypeId] = useState('');
  const [preferredDate, setPreferredDate] = useState('');
  const [preferredTime, setPreferredTime] = useState('');
  const [hours, setHours] = useState('');
  const [notes, setNotes] = useState('');

  // Retired addresses are not offered — the server refuses them anyway, and a
  // dropdown that lists a choice the server will reject is a trap.
  const usable = (properties.data?.data ?? []).filter((entry) => entry.isActive);
  const error = create.error instanceof ApiError ? create.error : null;
  const fieldErrors = error?.fieldErrors ?? {};

  function submit(event: FormEvent) {
    event.preventDefault();

    create.mutate(
      {
        propertyId,
        serviceTypeId: serviceTypeId === '' ? null : serviceTypeId,
        preferredDate: preferredDate === '' ? null : preferredDate,
        preferredTime: preferredTime === '' ? null : preferredTime,
        // Hours in, minutes stored — the conversion lives in the schema module
        // so both sides round the same way.
        estimatedDurationHours: hours === '' ? null : Number(hours),
        notes,
      },
      {
        onSuccess: () => {
          toast.success('Request sent. We will be in touch to confirm a date.');
          setServiceTypeId('');
          setPreferredDate('');
          setPreferredTime('');
          setHours('');
          setNotes('');
        },
      },
    );
  }

  if (properties.isPending) {
    return (
      <div aria-busy="true">
        <span className="sr-only">Loading the form</span>
        <div aria-hidden="true" className="h-72 animate-pulse rounded-lg bg-ink-100" />
      </div>
    );
  }

  if (usable.length === 0) {
    // A request needs somewhere to send a cleaner. Saying that, with the way
    // out, beats a form whose first field is empty and cannot be filled.
    return (
      <div className="rounded-lg border border-ink-300 bg-white shadow-card">
        <EmptyState
          icon={Home}
          title="Add an address first."
          hint="We need to know where to send your cleaner before you can ask for a clean."
          action={
            <Button asChild>
              <Link to="/portal/properties">
                <Home aria-hidden="true" />
                Add an address
              </Link>
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="space-y-4 rounded-lg border border-ink-300 bg-white p-4 shadow-card"
    >
      <RequiredFieldsNote />

      {error && Object.keys(fieldErrors).length === 0 ? (
        <FormError>{error.message}</FormError>
      ) : null}

      <div className="space-y-1.5">
        <Label htmlFor="request-property" required>
          Which address
        </Label>
        <select
          id="request-property"
          name="propertyId"
          required
          value={propertyId}
          onChange={(event) => {
            setPropertyId(event.target.value);
          }}
          className="min-h-control w-full rounded-sm border border-ink-300 bg-white px-3 text-body"
        >
          <option value="">Choose an address</option>
          {usable.map((entry) => (
            <option key={entry.id} value={entry.id}>
              {entry.nickname ? `${entry.nickname} — ` : ''}
              {entry.fullAddress}
            </option>
          ))}
        </select>
        {fieldErrors.propertyId ? (
          <p role="alert" className="text-meta text-status-priority-text">
            {fieldErrors.propertyId}
          </p>
        ) : null}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="request-service">What kind of clean</Label>
        <select
          id="request-service"
          name="serviceTypeId"
          value={serviceTypeId}
          onChange={(event) => {
            setServiceTypeId(event.target.value);
          }}
          className="min-h-control w-full rounded-sm border border-ink-300 bg-white px-3 text-body"
        >
          <option value="">Not sure — you tell me</option>
          {(services.data?.data ?? []).map((entry) => (
            <option key={entry.id} value={entry.id}>
              {entry.name}
            </option>
          ))}
        </select>
        <p className="text-meta text-ink-500">
          Leave it blank if you are not sure and we will suggest one.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label="Preferred date"
          name="preferredDate"
          type="date"
          value={preferredDate}
          error={fieldErrors.preferredDate}
          onChange={(event) => {
            setPreferredDate(event.target.value);
          }}
        />
        <Field
          label="Preferred time"
          name="preferredTime"
          type="time"
          value={preferredTime}
          error={fieldErrors.preferredTime}
          onChange={(event) => {
            setPreferredTime(event.target.value);
          }}
        />
      </div>

      <Field
        label="How long do you think it needs?"
        name="estimatedDurationHours"
        type="number"
        min={0.25}
        max={24}
        step={0.25}
        value={hours}
        error={fieldErrors.estimatedDurationHours}
        onChange={(event) => {
          setHours(event.target.value);
        }}
        placeholder="e.g. 2.5"
        // The prototype's own wording, and it is the right wording — a required
        // guess is a guess somebody gets wrong and then feels held to.
        hint="In hours. Optional — leave blank if unsure."
      />

      <div className="space-y-1.5">
        <Label htmlFor="request-notes">Anything else we should know</Label>
        <textarea
          id="request-notes"
          name="notes"
          rows={3}
          value={notes}
          onChange={(event) => {
            setNotes(event.target.value);
          }}
          className="w-full rounded-sm border border-ink-300 bg-white px-3 py-2 text-body"
          placeholder="Focus areas, pets, allergies, access notes…"
        />
        {fieldErrors.notes ? (
          <p role="alert" className="text-meta text-status-priority-text">
            {fieldErrors.notes}
          </p>
        ) : null}
      </div>

      <Button type="submit" full disabled={create.isPending}>
        <CalendarPlus aria-hidden="true" />
        {create.isPending ? 'Sending…' : 'Send request'}
      </Button>

      <p className="text-meta text-ink-500">
        Nothing is booked until we confirm it. You will see this request appear beside the form, and
        we will come back to you with a date.
      </p>
    </form>
  );
}
